'use client';

import { c as _c } from "react/compiler-runtime";
import type { InterfaceInputToken, Point, Rewards } from '@morpho-org/dapp-core';
import { Avatar, UserPositionIndicator } from '@morpho-org/ui-v6/client';
import { V1Badge } from '@morpho-org/ui-v6/server';
import { formatters } from '@morpho-org/ui-v6/utils';
import { Icon } from '@repo/ui';
import { ArrowDiagonalUp } from '@repo/ui/icons';
import { useMemo } from 'react';
import type { MerklTarget } from '@/components/common/RewardsHoverCard/MerklRewardsHoverCard';
import type { VaultV2ApyBreakdown } from '@/lib/procedures/vaultV2Apy.contract';
import { IconRewardHover } from '@/components/common/IconRewardHover';
import { InternalLink } from '@/components/common/InternalLink';
import { type Warning, WarningIcon } from '@/components/common/WarningIcon';
import { getVaultRowDisplayedNetApy, mapVaultRowApyCardRows } from '@/helpers/vaultApyCardRows';
interface MobileVaultCardHeaderProps {
  vaultName: string;
  asset: InterfaceInputToken;
  rewards: Rewards[];
  points: Point[];
  netApy: bigint | null | undefined;
  apy: bigint | null | undefined;
  performanceFee: bigint | null | undefined;
  managementFee: bigint | null | undefined;
  maxRate: bigint | null | undefined;
  maxApy?: number | null;
  isRateCapped?: boolean;
  includesDonations?: boolean;
  isVaultV2: boolean;
  href: string;
  hasUserPosition?: boolean;
  warnings: Warning[];
  merklContext?: MerklTarget | null;
  /** Set for V2 vaults (null when the split is unknown); undefined keeps the V1 card. */
  apyBreakdown?: VaultV2ApyBreakdown | null;
}
export default function MobileVaultCardHeader(t0) {
  const $ = _c(56);
  const {
    vaultName,
    asset,
    rewards,
    points,
    netApy,
    apy,
    performanceFee,
    managementFee,
    maxRate,
    maxApy,
    isRateCapped,
    includesDonations,
    isVaultV2,
    href,
    hasUserPosition: t1,
    warnings,
    merklContext,
    apyBreakdown
  } = t0;
  const hasUserPosition = t1 === undefined ? false : t1;
  const logoUri = asset.logoURI;
  let t2;
  if ($[0] !== apy || $[1] !== apyBreakdown || $[2] !== includesDonations || $[3] !== isRateCapped || $[4] !== maxApy || $[5] !== netApy || $[6] !== rewards) {
    t2 = apyBreakdown === undefined ? null : getVaultRowDisplayedNetApy(mapVaultRowApyCardRows({
      breakdown: apyBreakdown,
      apy,
      netApy,
      rewards,
      includesDonations,
      isRateCapped,
      maxApy
    }), apyBreakdown);
    $[0] = apy;
    $[1] = apyBreakdown;
    $[2] = includesDonations;
    $[3] = isRateCapped;
    $[4] = maxApy;
    $[5] = netApy;
    $[6] = rewards;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const reconciledNetApy = t2;
  const displayedNetApy = reconciledNetApy ?? netApy;
  let t3;
  if ($[8] !== displayedNetApy) {
    t3 = formatters.percent.digits(2).default("0.00%").unit("%").max(10).of(displayedNetApy, 18);
    $[8] = displayedNetApy;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const formattedNetApy = t3;
  const t4 = `View details for ${vaultName} vault${isVaultV2 ? "" : " (Morpho Vault V1)"}`;
  let t5;
  if ($[10] !== logoUri) {
    t5 = logoUri && <Avatar src={logoUri} size="medium" />;
    $[10] = logoUri;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== vaultName) {
    t6 = <span className="min-w-0 truncate text-xs leading-normal">{vaultName}</span>;
    $[12] = vaultName;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== isVaultV2) {
    t7 = !isVaultV2 && <V1Badge tabIndex={-1} />;
    $[14] = isVaultV2;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Icon className="text-foreground" icon={<ArrowDiagonalUp />} />;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t5 || $[18] !== t6 || $[19] !== t7) {
    t9 = <div className="gap-2xs flex items-center overflow-hidden whitespace-nowrap">{t5}{t6}{t7}{t8}</div>;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== href || $[22] !== t4 || $[23] !== t9) {
    t10 = <InternalLink className="min-w-0" href={href} prefetch={true} aria-label={t4}>{t9}</InternalLink>;
    $[21] = href;
    $[22] = t4;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== warnings) {
    t11 = <WarningIcon warnings={warnings} />;
    $[25] = warnings;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== hasUserPosition) {
    t12 = hasUserPosition && <UserPositionIndicator variant="vault" />;
    $[27] = hasUserPosition;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t10 || $[30] !== t11 || $[31] !== t12) {
    t13 = <div className="gap-xs flex min-w-0 items-center">{t10}{t11}{t12}</div>;
    $[29] = t10;
    $[30] = t11;
    $[31] = t12;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== apy || $[34] !== apyBreakdown || $[35] !== asset || $[36] !== includesDonations || $[37] !== isRateCapped || $[38] !== managementFee || $[39] !== maxApy || $[40] !== maxRate || $[41] !== netApy || $[42] !== performanceFee || $[43] !== points || $[44] !== rewards) {
    t14 = {
      performanceFee,
      managementFee,
      maxRate,
      maxApy,
      isRateCapped,
      includesDonations,
      apyBreakdown,
      apy,
      rewards,
      points,
      variant: "vault",
      asset,
      netApy
    };
    $[33] = apy;
    $[34] = apyBreakdown;
    $[35] = asset;
    $[36] = includesDonations;
    $[37] = isRateCapped;
    $[38] = managementFee;
    $[39] = maxApy;
    $[40] = maxRate;
    $[41] = netApy;
    $[42] = performanceFee;
    $[43] = points;
    $[44] = rewards;
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  let t15;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      display: "flex",
      alignItems: "center",
      lineHeight: "normal"
    };
    $[46] = t15;
  } else {
    t15 = $[46];
  }
  let t16;
  if ($[47] !== formattedNetApy) {
    t16 = <span className="text-foreground text-xs" style={t15}>{formattedNetApy}</span>;
    $[47] = formattedNetApy;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  let t17;
  if ($[49] !== merklContext || $[50] !== t14 || $[51] !== t16) {
    t17 = <div className="flex shrink-0 items-center gap-[2px]"><IconRewardHover isMobileSupportEnabled={true} isListRow={true} merklContext={merklContext} RewardsHoverCardProps={t14}>{t16}</IconRewardHover></div>;
    $[49] = merklContext;
    $[50] = t14;
    $[51] = t16;
    $[52] = t17;
  } else {
    t17 = $[52];
  }
  let t18;
  if ($[53] !== t13 || $[54] !== t17) {
    t18 = <div className="flex w-full items-center justify-between overflow-hidden">{t13}{t17}</div>;
    $[53] = t13;
    $[54] = t17;
    $[55] = t18;
  } else {
    t18 = $[55];
  }
  return t18;
}